import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { SafeImage } from '@/components/ui/safe-image';
import { ExternalLink, Pause, Play, RotateCcw, RotateCw, SkipBack, SkipForward } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import type { AisleWalkthrough, WalkthroughMarker } from '../types';
import { ComplianceBadge } from './ComplianceBadge';
import { resolveWalkthroughVideoUrl } from '../mock-data/walkthrough-videos';
import {
  nextVideoRotationDegrees,
  walkthroughBrowserRotationDegrees,
  walkthroughVideoObjectClass,
  walkthroughVideoRotationStyle,
  type VideoRotationDegrees,
} from '../lib/walkthroughVideoRotation';
import { captureWalkthroughThumbnailFromRemoteUrl, captureWalkthroughThumbnailFromVideoElement } from '../lib/captureWalkthroughThumbnail';
import {
  assessWalkthroughDuration,
  assessWalkthroughPlaybackPace,
} from '../lib/walkthroughVideoHealth';

interface WalkthroughVideoPlayerProps {
  walk: AisleWalkthrough;
  activeMarker: WalkthroughMarker | null;
  onMarkerChange: (marker: WalkthroughMarker | null) => void;
  onMarkerClick: (marker: WalkthroughMarker) => void;
  /** When set, player seeks to this timestamp (e.g. from side-panel list) */
  seekToSeconds?: number | null;
  /** Called after an external seek request has been applied */
  onSeekHandled?: () => void;
  storeId?: number;
  onRotationChange?: (degrees: VideoRotationDegrees) => void;
  onThumbnailCaptured?: () => void;
}

function formatDuration(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds >= 0 ? seconds : 0;
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function isQuickTimeSource(url: string | undefined): boolean {
  if (!url) return false;
  return /\.mov(\?|$)/i.test(url) || url.includes('video/quicktime');
}

function describeVideoError(code: number | undefined): string {
  switch (code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return 'Playback was aborted.';
    case MediaError.MEDIA_ERR_NETWORK:
      return 'Network error while loading the video.';
    case MediaError.MEDIA_ERR_DECODE:
      return 'This video format cannot be decoded in your browser.';
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'This video format is not supported in your browser.';
    default:
      return 'Could not load the walk-through video.';
  }
}

function walkthroughVideoIdentity(walk: AisleWalkthrough, videoUrl?: string): string {
  if (walk.videoStoragePath) {
    return `${walk.id}:${walk.videoStoragePath}`;
  }
  if (videoUrl) {
    return `${walk.id}:${videoUrl.split('?')[0]}`;
  }
  return walk.id;
}

export function WalkthroughVideoPlayer({
  walk,
  activeMarker,
  onMarkerChange,
  onMarkerClick,
  seekToSeconds,
  onSeekHandled,
  storeId,
  onRotationChange,
  onThumbnailCaptured,
}: WalkthroughVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeMarkerIdRef = useRef<string | null>(activeMarker?.id ?? null);
  const appliedSeekRef = useRef<{ walkId: string; seconds: number } | null>(null);
  const thumbnailCaptureAttemptedRef = useRef<string | null>(null);
  const durationPersistedRef = useRef<string | null>(null);
  const playbackSampleRef = useRef<{ videoTime: number; wallTime: number } | null>(null);
  const videoUrl = resolveWalkthroughVideoUrl(walk.id, walk.videoUrl);
  const videoIdentity = walkthroughVideoIdentity(walk, videoUrl);
  const [hasVideo, setHasVideo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(walk.durationSeconds ?? 0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(videoUrl));
  const [localPosterUrl, setLocalPosterUrl] = useState<string | null>(null);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [thumbnailRetryKey, setThumbnailRetryKey] = useState(0);

  const effectiveDuration = duration > 0 ? duration : (walk.durationSeconds ?? 0);
  const progress = effectiveDuration > 0 ? (currentTime / effectiveDuration) * 100 : 0;
  const quickTimeWarning = isQuickTimeSource(videoUrl ?? walk.videoUrl);
  const videoRotationDegrees = walkthroughBrowserRotationDegrees(
    videoUrl ?? walk.videoUrl,
    walk.videoRotationDegrees,
  );
  const videoObjectClass = walkthroughVideoObjectClass(videoRotationDegrees);
  const videoRotationStyle = walkthroughVideoRotationStyle(videoRotationDegrees);
  const posterUrl = localPosterUrl ?? (walk.hasRealThumbnail === false ? undefined : walk.thumbnailUrl);
  const canAdjustOrientation = Boolean(
    storeId != null && walk.liveWalkthroughId != null && onRotationChange,
  );

  const [orientationControlsOpen, setOrientationControlsOpen] = useState(
    () => (walk.videoRotationDegrees ?? 0) === 0,
  );

  useEffect(() => {
    // Collapse once a correction is saved; keep open when still at 0°.
    if (videoRotationDegrees !== 0) {
      setOrientationControlsOpen(false);
    }
  }, [videoRotationDegrees]);

  useEffect(() => {
    activeMarkerIdRef.current = activeMarker?.id ?? null;
  }, [activeMarker?.id]);

  useEffect(() => {
    return () => {
      if (localPosterUrl) {
        URL.revokeObjectURL(localPosterUrl);
      }
    };
  }, [localPosterUrl]);

  const syncMarkerForTime = useCallback((timeSeconds: number) => {
    const sorted = [...walk.timelineMarkers].sort((a, b) => a.timestampSeconds - b.timestampSeconds);
    let match: WalkthroughMarker | null = null;
    for (const marker of sorted) {
      if (timeSeconds >= marker.timestampSeconds - 0.5) {
        match = marker;
      } else {
        break;
      }
    }
    const nextMarkerId = match?.id ?? null;
    if (nextMarkerId === activeMarkerIdRef.current) {
      return;
    }
    activeMarkerIdRef.current = nextMarkerId;
    onMarkerChange(match);
  }, [onMarkerChange, walk.timelineMarkers]);

  useEffect(() => {
    appliedSeekRef.current = null;
    thumbnailCaptureAttemptedRef.current = null;
    durationPersistedRef.current = null;
    playbackSampleRef.current = null;
    setHasVideo(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setLoadError(null);
    setIsLoading(Boolean(videoUrl));
    setThumbnailFailed(false);
    setLocalPosterUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return null;
    });
    setDuration(walk.durationSeconds ?? 0);
  }, [videoIdentity, walk.durationSeconds, videoUrl]);

  const persistThumbnail = useCallback(async (blob: Blob) => {
    if (storeId == null || walk.liveWalkthroughId == null) return;
    try {
      const formData = new FormData();
      formData.append('thumbnail', blob, 'thumbnail.jpg');
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${walk.liveWalkthroughId}/thumbnail`,
        'POST',
        formData,
      );
      if (response.ok) {
        setThumbnailFailed(false);
        onThumbnailCaptured?.();
      } else {
        setThumbnailFailed(true);
      }
    } catch (error) {
      // Never let a poster-upload failure escape as an unhandled rejection —
      // surface it as a visible processing failure with a retry path instead.
      console.warn('[STORE_PROFILES] Walkthrough thumbnail upload failed:', error);
      setThumbnailFailed(true);
    }
  }, [onThumbnailCaptured, storeId, walk.liveWalkthroughId]);

  const maybeCaptureThumbnail = useCallback(async () => {
    if (walk.dataSource !== 'live' || walk.liveWalkthroughId == null || storeId == null) return;
    if (walk.hasRealThumbnail && videoRotationDegrees === 0) return;
    const attemptKey = `${videoIdentity}:rot${videoRotationDegrees}:server:retry${thumbnailRetryKey}`;
    if (thumbnailCaptureAttemptedRef.current === attemptKey) return;
    thumbnailCaptureAttemptedRef.current = attemptKey;

    try {
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${walk.liveWalkthroughId}/generate-thumbnail`,
        'POST',
        { videoRotationDegrees },
      );
      if (response.ok) {
        setThumbnailFailed(false);
        onThumbnailCaptured?.();
        return;
      }
    } catch {
      // Server-side generation unavailable — fall through to client capture.
    }

    const video = videoRef.current;
    if (!video || !hasVideo) {
      thumbnailCaptureAttemptedRef.current = null;
      return;
    }

    let blob: Blob | null = null;
    try {
      blob = await captureWalkthroughThumbnailFromVideoElement(video, videoRotationDegrees);
      if (!blob && videoUrl) {
        blob = await captureWalkthroughThumbnailFromRemoteUrl(videoUrl, videoRotationDegrees);
      }
    } catch (error) {
      console.warn('[STORE_PROFILES] Walkthrough thumbnail capture failed:', error);
    }
    if (!blob) {
      thumbnailCaptureAttemptedRef.current = null;
      setThumbnailFailed(true);
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    setLocalPosterUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return objectUrl;
    });

    await persistThumbnail(blob);
  }, [
    hasVideo,
    onThumbnailCaptured,
    persistThumbnail,
    storeId,
    thumbnailRetryKey,
    videoIdentity,
    videoRotationDegrees,
    videoUrl,
    walk.dataSource,
    walk.hasRealThumbnail,
    walk.liveWalkthroughId,
  ]);

  /** Persist the browser-measured duration so cards never show a fabricated 0:00. */
  const maybePersistDuration = useCallback(async (measuredSeconds: number) => {
    if (walk.dataSource !== 'live' || walk.liveWalkthroughId == null || storeId == null) return;
    if (walk.durationSeconds != null && walk.durationSeconds > 0) return;
    const key = `${videoIdentity}:${measuredSeconds}`;
    if (durationPersistedRef.current === key) return;
    durationPersistedRef.current = key;
    try {
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${walk.liveWalkthroughId}`,
        'PATCH',
        { durationSeconds: measuredSeconds },
      );
      if (response.ok) {
        onThumbnailCaptured?.(); // revalidate walkthrough queries
      }
    } catch (error) {
      durationPersistedRef.current = null;
      console.warn('[STORE_PROFILES] Failed to persist walkthrough duration:', error);
    }
  }, [onThumbnailCaptured, storeId, videoIdentity, walk.dataSource, walk.durationSeconds, walk.liveWalkthroughId]);

  const markReady = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setIsLoading(false);
    const assessment = assessWalkthroughDuration(video.duration, walk.durationSeconds);
    setDuration(assessment.displaySeconds);
    if (!assessment.usable) {
      video.pause();
      setIsPlaying(false);
      setHasVideo(false);
      setLoadError(assessment.message);
      return;
    }

    setHasVideo(true);
    setLoadError(null);
    if (assessment.measuredSeconds != null) {
      void maybePersistDuration(assessment.measuredSeconds);
    }
  }, [maybePersistDuration, walk.durationSeconds]);

  useEffect(() => {
    if (!hasVideo) return;
    maybeCaptureThumbnail().catch((error) => {
      console.warn('[STORE_PROFILES] Walkthrough thumbnail pipeline failed:', error);
      setThumbnailFailed(true);
    });
  }, [hasVideo, maybeCaptureThumbnail]);

  const seekTo = useCallback((seconds: number, updateVideo = true) => {
    const video = videoRef.current;
    const maxDuration = effectiveDuration > 0
      ? effectiveDuration
      : (video && Number.isFinite(video.duration) && video.duration > 0 ? video.duration : seconds);
    const clamped = Math.max(0, Math.min(seconds, maxDuration));
    if (video && hasVideo && updateVideo) {
      if (Math.abs(video.currentTime - clamped) > 0.05) {
        video.currentTime = clamped;
      }
    }
    playbackSampleRef.current = null;
    setCurrentTime(clamped);
    syncMarkerForTime(clamped);
  }, [effectiveDuration, hasVideo, syncMarkerForTime]);

  useEffect(() => {
    if (seekToSeconds == null) {
      appliedSeekRef.current = null;
      return;
    }
    if (!hasVideo) {
      return;
    }
    const alreadyApplied = appliedSeekRef.current?.walkId === walk.id
      && appliedSeekRef.current.seconds === seekToSeconds;
    if (alreadyApplied) {
      return;
    }

    seekTo(seekToSeconds);
    appliedSeekRef.current = { walkId: walk.id, seconds: seekToSeconds };

    const marker = walk.timelineMarkers.find((m) => m.timestampSeconds === seekToSeconds);
    if (marker) {
      onMarkerClick(marker);
    }
    onSeekHandled?.();
  }, [seekToSeconds, walk.id, hasVideo, seekTo, walk.timelineMarkers, onMarkerClick, onSeekHandled]);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    const now = performance.now();
    const previous = playbackSampleRef.current;
    if (previous && video.currentTime >= previous.videoTime) {
      const pace = assessWalkthroughPlaybackPace(
        video.currentTime - previous.videoTime,
        now - previous.wallTime,
      );
      if (!pace.usable) {
        video.pause();
        setIsPlaying(false);
        setHasVideo(false);
        setLoadError(
          'This video has invalid playback timing and cannot be used as a store tour. Re-upload the original as an H.264 MP4.',
        );
        playbackSampleRef.current = null;
        return;
      }
    }
    setCurrentTime(video.currentTime);
    syncMarkerForTime(video.currentTime);
  };

  const handleVideoError = () => {
    const video = videoRef.current;
    setHasVideo(false);
    setIsLoading(false);
    setLoadError(describeVideoError(video?.error?.code));
  };

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video || !hasVideo) return;
    if (video.paused) {
      try {
        await video.play();
        setIsPlaying(true);
      } catch (error) {
        // play() rejects on interruption/autoplay policy — not an app error.
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.warn('[STORE_PROFILES] Walkthrough playback failed:', error);
        setLoadError('Playback could not start. Try reloading the page.');
      }
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleSkipBack = () => seekTo(0);
  const handleSkipForward = () => {
    const video = videoRef.current;
    const end = effectiveDuration > 0
      ? effectiveDuration
      : (video && Number.isFinite(video.duration) ? video.duration : 0);
    seekTo(end);
  };

  const handleMarkerClick = (marker: WalkthroughMarker) => {
    seekTo(marker.timestampSeconds);
    onMarkerClick(marker);
  };

  return (
    <div className="space-y-2">
      <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
        {videoUrl && (
          <video
            ref={videoRef}
            key={videoIdentity}
            src={videoUrl}
            poster={posterUrl}
            className={`w-full h-full ${videoObjectClass} transition-opacity ${hasVideo ? 'opacity-100' : 'opacity-0'}`}
            style={videoRotationStyle}
            preload="metadata"
            playsInline
            onLoadedMetadata={markReady}
            onLoadedData={markReady}
            onCanPlay={markReady}
            onError={handleVideoError}
            onTimeUpdate={handleTimeUpdate}
            onPlay={() => {
              playbackSampleRef.current = {
                videoTime: videoRef.current?.currentTime ?? 0,
                wallTime: performance.now(),
              };
              setIsPlaying(true);
            }}
            onPause={() => {
              playbackSampleRef.current = null;
              setIsPlaying(false);
            }}
            onEnded={() => {
              playbackSampleRef.current = null;
              setIsPlaying(false);
            }}
          />
        )}

        {(!videoUrl || !hasVideo) && (
          <SafeImage
            src={posterUrl ?? walk.thumbnailUrl}
            alt="Walk-through preview"
            className="absolute inset-0 w-full h-full object-cover opacity-80"
            placeholderType="photo"
          />
        )}

        {isLoading && !loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-sm">
            Loading walk-through…
          </div>
        )}

        {loadError && (
          <div className="absolute top-3 left-3 right-3 bg-black/80 text-white text-xs rounded-md px-3 py-2 space-y-2 z-20">
            <p>{loadError}</p>
            {quickTimeWarning && (
              <p>
                iPhone <strong>.MOV</strong> files often fail in Chrome/Edge. Re-upload as <strong>.MP4</strong> or open in Safari.
              </p>
            )}
            {videoUrl && (
              <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="underline">
                Open video in new tab
              </a>
            )}
          </div>
        )}

        {activeMarker && (
          <div className="absolute top-4 left-4 right-4 bg-white/95 backdrop-blur rounded-lg p-3 shadow-lg border max-w-sm z-10">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-sm">{activeMarker.label ?? activeMarker.campaignName}</p>
                {(activeMarker.brand || activeMarker.formatLabel) && (
                  <p className="text-xs text-gray-500">{activeMarker.brand} · {activeMarker.formatLabel}</p>
                )}
              </div>
              {activeMarker.complianceStatus !== 'unavailable' && (
                <ComplianceBadge status={activeMarker.complianceStatus} />
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">At {formatDuration(activeMarker.timestampSeconds)}</p>
            {activeMarker.evidenceRoute && (
              <Link href={activeMarker.evidenceRoute}>
                <Button variant="link" size="sm" className="h-auto p-0 mt-1">
                  <ExternalLink className="h-3 w-3 mr-1" /> View evidence
                </Button>
              </Link>
            )}
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 z-10">
          <div className="flex items-center gap-3 text-white mb-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={handleSkipBack}
              aria-label="Restart walk-through"
            >
              <SkipBack className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={togglePlay}
              disabled={!hasVideo}
              aria-label={isPlaying ? 'Pause walk-through' : 'Play walk-through'}
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={handleSkipForward}
              aria-label="Skip to end"
            >
              <SkipForward className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              {formatDuration(currentTime)} / {effectiveDuration > 0 ? formatDuration(effectiveDuration) : '—'}
            </span>
          </div>
          <div className="relative">
            <Progress value={progress} className="h-2" />
            {walk.timelineMarkers.map((marker) => (
              <button
                key={marker.id}
                type="button"
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-blue-500 border-2 border-white hover:scale-125 transition-transform z-20"
                style={{ left: `${effectiveDuration > 0 ? (marker.timestampSeconds / effectiveDuration) * 100 : 0}%` }}
                onClick={() => handleMarkerClick(marker)}
                title={`${marker.campaignName} @ ${formatDuration(marker.timestampSeconds)}`}
              />
            ))}
          </div>
        </div>
      </div>

      {thumbnailFailed && (
        <div
          role="alert"
          className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
        >
          <span className="text-xs text-amber-900">
            Preview image could not be generated. Playback is unaffected.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              thumbnailCaptureAttemptedRef.current = null;
              setThumbnailFailed(false);
              setThumbnailRetryKey((key) => key + 1);
            }}
          >
            Retry preview
          </Button>
        </div>
      )}

      {canAdjustOrientation && orientationControlsOpen && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <span className="text-xs text-amber-900">
            {videoRotationDegrees === 0
              ? 'Looks upside down or sideways? Chrome often ignores iPhone rotation tags.'
              : `${videoRotationDegrees}° orientation applied.`}
          </span>
          <Button
            type="button"
            variant={videoRotationDegrees === 180 ? 'outline' : 'default'}
            size="sm"
            disabled={videoRotationDegrees === 180}
            onClick={() => onRotationChange?.(180)}
          >
            Fix upside down
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onRotationChange?.(nextVideoRotationDegrees(videoRotationDegrees, -90))}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            Left
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onRotationChange?.(nextVideoRotationDegrees(videoRotationDegrees, 90))}
          >
            <RotateCw className="h-3.5 w-3.5 mr-1" />
            Right
          </Button>
          {videoRotationDegrees !== 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setOrientationControlsOpen(false)}
            >
              Done
            </Button>
          )}
        </div>
      )}

      {canAdjustOrientation && !orientationControlsOpen && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-xs text-gray-500 hover:text-gray-800 underline-offset-2 hover:underline"
            onClick={() => setOrientationControlsOpen(true)}
          >
            {videoRotationDegrees === 0
              ? 'Adjust orientation'
              : `Orientation fixed (${videoRotationDegrees}°) · Adjust`}
          </button>
        </div>
      )}
    </div>
  );
}

export { formatDuration as formatWalkthroughDuration };
